import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import {
  Headset,
  Plus,
  Search,
  X,
  Ticket,
  HelpCircle,
  ChevronRight,
  ChevronLeft,
  Book,
  ArrowRight,
  ArrowLeft,
  PlusCircle,
  ArrowLeftRight,
  Link2,
  MessageSquareText,
  Circle,
  FileText,
  Clock,
  Send,
  Paperclip,
  FileImage,
  XCircle,
  Loader2,
  ListOrdered,
  Info,
  ChevronDown,
  ChevronUp,
  Reply,
  User,
  Settings,
  Flag,
  Inbox,
  BarChart3,
  Menu,
  Mail
} from 'lucide-react';
import {
  getSupportTickets,
  getSupportTicketStats,
  createSupportTicket,
  getSupportTicketById,
  addCommentToTicket,
  uploadAttachmentToTicket,
  SupportTicket,
  TicketStats,
  CreateTicketRequest,
  TicketFilters,
  TicketHistory,
  JiraDetails
} from 'services/support/supportService';
import helpDataEn from 'data/help/helpData.en.json';
import helpDataFr from 'data/help/helpData.fr.json';
import {
  formatDate,
  getStatusBadge,
  getPriorityBadge,
  getCategoryIcon,
  getEventIcon,
  getEventColor,
  filterArticles,
} from './support.utils';
import LoadingSpinnerOverlay from './components/LoadingSpinnerOverlay';
import { getErrorMessage } from 'utils/errors';

interface ArticleStep {
  title?: string;
  content?: string;
  description?: string;
  image?: string;
  video?: string;
  [key: string]: unknown;
}

interface Article {
  id: string;
  title: string;
  content: string;
  steps: ArticleStep[];
}

interface Category {
  id: string;
  title: string;
  icon: string;
  description: string;
  articles: Article[];
}

const Support: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState<boolean>(true);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const [ticketSearchQuery, setTicketSearchQuery] = useState<string>('');
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketHistory, setTicketHistory] = useState<TicketHistory[]>([]);
  const [jiraDetails, setJiraDetails] = useState<JiraDetails | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isLoadingTicket, setIsLoadingTicket] = useState<boolean>(false);
  const [filters, setFilters] = useState<TicketFilters>({
    status: '',
    category: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
    limit: 10
  });

  // Tab state
  const [activeTab, setActiveTab] = useState<'tickets' | 'help'>('tickets');

  // Help section states (right sidebar)
  const [helpSearchQuery, setHelpSearchQuery] = useState<string>('');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showArticleModal, setShowArticleModal] = useState<boolean>(false);

  // Help Center tab states
  const [selectedHelpCategory, setSelectedHelpCategory] = useState<Category | null>(null);
  const [selectedHelpArticle, setSelectedHelpArticle] = useState<Article | null>(null);
  const [helpCategorySearchQuery, setHelpCategorySearchQuery] = useState<string>('');

  // Comment and attachment states
  const [comment, setComment] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false);
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [showReplySection, setShowReplySection] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get help data based on language
  const helpData = i18n.language === 'fr' ? helpDataFr : helpDataEn;

  // Form state for creating ticket
  const [formData, setFormData] = useState<CreateTicketRequest>({
    category: 'technical_issue',
    subject: '',
    description: '',
    priority: 'medium'
  });

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark
    ? '0px 1px 2px rgba(2, 6, 24, 0.5)'
    : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const cardBg = isDark ? '#0F172B' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const labelColor = isDark ? '#E2E8F0' : '#314158';
  const mutedText = isDark ? '#90A1B9' : '#62748E';
  const hoverBg = isDark ? '#1D293D' : '#F8FAFC';
  const dividerColor = isDark ? '#1D293D' : '#F1F5F9';
  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const modalBorder = isDark ? '#314158' : '#E2E8F0';
  const modalFooterBg = isDark ? '#1D293D' : '#F8FAFC';
  const buttonBg = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const secondaryButtonBg = isDark ? '#1D293D' : '#FFFFFF';
  const secondaryButtonBorder = isDark ? '#314158' : '#E2E8F0';
  const secondaryButtonText = isDark ? '#E2E8F0' : '#314158';
  const disabledOpacity = 0.5;
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const infoText = isDark ? '#93C5FD' : '#1E40AF';
  const commentBg = isDark ? '#162456' : '#EEF2FF';
  const commentBorder = isDark ? '#1E3A8A' : '#C7D2FE';
  const changeBg = isDark ? '#162456' : '#EFF6FF';
  const changeBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const jiraBg = isDark ? '#064E3B' : '#ECFDF5';
  const jiraBorder = isDark ? '#10B981' : '#A7F3D0';
  const jiraText = isDark ? '#6EE7B7' : '#065F46';
  const fileBg = isDark ? '#162456' : '#EFF6FF';
  const fileBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  // Help Center tab tokens
  const activeItemBg = isDark ? '#162456' : '#EFF6FF';
  const activeItemBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const activeItemText = isDark ? '#51A2FF' : '#2B7FFF';
  const stepBorderColor = isDark ? '#314158' : '#BFDBFE';

  useEffect(() => {
    fetchSupportData();
  }, []);

  // Refetch when filters or page change
  useEffect(() => {
    if (currentPage > 1 || filters.status || filters.category) {
      fetchSupportData();
    }
  }, [currentPage, filters.status, filters.category]);

  const fetchSupportData = async () => {
    try {
      setLoading(true);

      const [ticketsResponse, statsResponse] = await Promise.all([
        getSupportTickets({ ...filters, page: currentPage }),
        getSupportTicketStats()
      ]);

      setTickets(ticketsResponse.tickets || []);
      setStats(statsResponse.stats || statsResponse.data || null);
      if (ticketsResponse.pagination) {
        setTotalPages(ticketsResponse.pagination.totalPages);
      }
    } catch (error: unknown) {
      const msg = getErrorMessage(error, t('support.errors.failedToLoad') || 'Failed to load support data');
      if (msg.includes('No authentication token')) {
        toast.error(t('support.errors.sessionExpired') || 'Session expired');
        navigate('/');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error(t('support.errors.fillRequired') || 'Please fill in all required fields');
      return;
    }

    try {
      setIsCreating(true);
      await createSupportTicket(formData);
      toast.success(t('support.ticketCreated') || 'Support ticket created successfully');
      setShowCreateModal(false);
      setFormData({
        category: 'technical_issue',
        subject: '',
        description: '',
        priority: 'medium'
      });
      fetchSupportData();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t('support.errors.failedToCreate')));
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewTicket = async (ticketId: string) => {
    try {
      setIsLoadingTicket(true);
      const response = await getSupportTicketById(ticketId);
      setSelectedTicket(response.ticket || response.data || null);
      setTicketHistory(response.history || []);
      setJiraDetails(response.jira_details || null);
      setShowDetailsModal(true);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t('support.errors.failedToFetch')));
    } finally {
      setIsLoadingTicket(false);
    }
  };

  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
  };

  const handleProceedToCreateTicket = () => {
    setShowSearchModal(false);
    setTicketSearchQuery('');
    setShowCreateModal(true);
  };

  // Help Center tab methods
  const filteredHelpCategories = (helpData.categories || []).filter((category: Category) =>
    category.title.toLowerCase().includes(helpCategorySearchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(helpCategorySearchQuery.toLowerCase())
  );

  const handleHelpCategoryClick = (category: Category) => {
    setSelectedHelpCategory(category);
    setSelectedHelpArticle(category.articles[0] || null);
  };

  const handleBackToHelpCategories = () => {
    setSelectedHelpCategory(null);
    setSelectedHelpArticle(null);
  };

  const handleContactSupport = () => {
    setActiveTab('tickets');
    setShowSearchModal(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('support.errors.onlyImages') || 'Only image files are allowed');
      return;
    }

    const maxSize = 1 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t('support.errors.fileTooLarge') || 'File size must be less than 1MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCommentSubmit = async () => {
    if (!selectedTicket) return;

    if (!comment.trim() && !selectedFile) {
      toast.error(t('support.errors.emptyComment') || 'Please enter a comment or attach a file');
      return;
    }

    try {
      setIsSubmittingComment(true);

      if (selectedFile) {
        setIsUploadingFile(true);
        await uploadAttachmentToTicket(selectedTicket._id, selectedFile);
        setIsUploadingFile(false);
        toast.success(t('support.attachmentUploaded') || 'Attachment uploaded successfully');
      }

      if (comment.trim()) {
        await addCommentToTicket(selectedTicket._id, comment);
        toast.success(t('support.commentAdded') || 'Comment added successfully');
      }

      const response = await getSupportTicketById(selectedTicket._id);
      setSelectedTicket(response.ticket || response.data || null);
      setTicketHistory(response.history || []);

      setComment('');
      setSelectedFile(null);
      setShowReplySection(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t('support.errors.failedToSubmit')));
    } finally {
      setIsSubmittingComment(false);
      setIsUploadingFile(false);
    }
  };

  if (loading) {
    return <Spinner size="large" text={t('common.loading')} fullPage />;
  }

  return (
    <div
      className="p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        height: '100%',
        gap: '8px',
        background: containerBg,
      }}
    >
      {/* Main Card */}
      <div
        className="rounded-none lg:rounded-2xl"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          flex: 1,
          width: '100%',
          overflow: 'hidden',
          background: mainBg,
          boxShadow: mainShadow,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
            flexShrink: 0,
            padding: '12px 16px',
            borderBottom: `1px solid ${headerBorder}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: '8px',
              height: '36px',
            }}
          >
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div
              className="hidden lg:flex"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: '36px',
                height: '36px',
                background: iconContainerBg,
                borderRadius: '12px',
              }}
            >
              <Headset size={24} style={{ color: iconColor }} />
            </div>
            <h1
              style={{
                fontFamily: 'Geist, sans-serif',
                fontWeight: 700,
                fontSize: '20px',
                lineHeight: '28px',
                color: titleColor,
                margin: 0,
                flex: 1,
              }}
            >
              {t('support.title')}
            </h1>

            {/* Create Ticket Button */}
            <button
              onClick={() => setShowSearchModal(true)}
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0,
                padding: '8px 12px',
                gap: '4px',
                height: '36px',
                background: buttonBg,
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                color: '#FFFFFF',
              }}
            >
              <Plus size={20} color="#FFFFFF" />
              <span
                style={{
                  fontFamily: 'Geist, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#FFFFFF',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('support.createTicket')}
              </span>
            </button>
          </div>
        </div>

        {/* Tab Toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderBottom: `1px solid ${headerBorder}`,
            flexShrink: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <button
            onClick={() => setActiveTab('tickets')}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 500,
              borderRadius: '8px',
              border: activeTab === 'tickets' ? 'none' : `1px solid ${secondaryButtonBorder}`,
              background: activeTab === 'tickets' ? buttonBg : 'transparent',
              color: activeTab === 'tickets' ? '#FFFFFF' : mutedText,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {t('support.tabs.myTickets')}
          </button>
          <button
            onClick={() => setActiveTab('help')}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 500,
              borderRadius: '8px',
              border: activeTab === 'help' ? 'none' : `1px solid ${secondaryButtonBorder}`,
              background: activeTab === 'help' ? buttonBg : 'transparent',
              color: activeTab === 'help' ? '#FFFFFF' : mutedText,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {t('support.tabs.helpCenter')}
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            position: 'relative',
            flex: 1,
            width: '100%',
            background: mainBg,
          }}
        >
          <div
            className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
            style={{
              position: 'absolute',
              inset: 0,
              overflowY: 'auto',
              padding: '16px',
            }}
          >
          {activeTab === 'tickets' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr]" style={{ gap: '24px' }}>
                {/* Left Column - Tickets */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Filters */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      padding: '16px',
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      borderRadius: '12px',
                    }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                        <span style={{ fontSize: '12px', fontWeight: 500, color: labelColor }}>
                          {t('support.filterStatus')}
                        </span>
                        <select
                          value={filters.status}
                          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: '12px',
                            border: `1px solid ${inputBorder}`,
                            borderRadius: '8px',
                            background: inputBg,
                            color: inputText,
                            outline: 'none',
                            cursor: 'pointer',
                            appearance: 'auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <option value="">{t('support.allStatuses')}</option>
                          <option value="open">{t('support.status.open')}</option>
                          <option value="in_progress">{t('support.status.in_progress')}</option>
                          <option value="waiting_for_customer">{t('support.status.waiting_for_customer')}</option>
                          <option value="resolved">{t('support.status.resolved')}</option>
                          <option value="closed">{t('support.status.closed')}</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                        <span style={{ fontSize: '12px', fontWeight: 500, color: labelColor }}>
                          {t('support.filterCategory')}
                        </span>
                        <select
                          value={filters.category}
                          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: '12px',
                            border: `1px solid ${inputBorder}`,
                            borderRadius: '8px',
                            background: inputBg,
                            color: inputText,
                            outline: 'none',
                            cursor: 'pointer',
                            appearance: 'auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <option value="">{t('support.allCategories')}</option>
                          <option value="technical_issue">{t('support.category.technical_issue')}</option>
                          <option value="billing">{t('support.category.billing')}</option>
                          <option value="account">{t('support.category.account')}</option>
                          <option value="feature_request">{t('support.category.feature_request')}</option>
                          <option value="bug_report">{t('support.category.bug_report')}</option>
                          <option value="general_inquiry">{t('support.category.general_inquiry')}</option>
                          <option value="other">{t('support.category.other')}</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                        <span style={{ fontSize: '12px', fontWeight: 500, color: labelColor }}>
                          {t('support.sortBy')}
                        </span>
                        <select
                          value={`${filters.sortBy}_${filters.sortOrder}`}
                          onChange={(e) => {
                            const [sortBy, sortOrder] = e.target.value.split('_');
                            setFilters({ ...filters, sortBy, sortOrder: sortOrder as 'asc' | 'desc' });
                          }}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: '12px',
                            border: `1px solid ${inputBorder}`,
                            borderRadius: '8px',
                            background: inputBg,
                            color: inputText,
                            outline: 'none',
                            cursor: 'pointer',
                            appearance: 'auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <option value="created_at_desc">{t('support.sort.newest')}</option>
                          <option value="created_at_asc">{t('support.sort.oldest')}</option>
                          <option value="updated_at_desc">{t('support.sort.recentlyUpdated')}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Tickets List */}
                  <div
                    style={{
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      borderRadius: '12px',
                      overflowX: 'auto',
                    }}
                  >
                   <div className="min-w-[500px] lg:min-w-0">
                    {/* List Header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        borderBottom: `1px solid ${sectionBorder}`,
                      }}
                    >
                      <Ticket size={16} style={{ color: iconColor }} />
                      <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>
                        {t('support.yourTickets')} ({tickets.length})
                      </span>
                    </div>

                    {/* Ticket Items */}
                    {tickets.length > 0 ? (
                      tickets.map((ticket, index) => {
                        const statusBadge = getStatusBadge(ticket.status, isDark);
                        const priorityBadge = getPriorityBadge(ticket.priority, isDark);
                        return (
                          <div
                            key={ticket._id}
                            onClick={() => handleViewTicket(ticket._id)}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              padding: '14px 16px',
                              cursor: 'pointer',
                              borderBottom: index < tickets.length - 1 ? `1px solid ${dividerColor}` : 'none',
                              transition: 'background 0.15s ease',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  width: '32px',
                                  height: '32px',
                                  background: iconContainerBg,
                                  borderRadius: '8px',
                                  color: iconColor,
                                  marginTop: '2px',
                                }}
                              >
                                {getCategoryIcon(ticket.category)}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: titleColor,
                                    marginBottom: '4px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {ticket.subject}
                                </div>
                                <div
                                  style={{
                                    fontSize: '13px',
                                    color: mutedText,
                                    marginBottom: '6px',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {ticket.description}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '12px', color: mutedText }}>
                                    #{ticket.ticket_id || ticket._id}
                                  </span>
                                  <span style={{ fontSize: '12px', color: isDark ? '#62748E' : '#90A1B9' }}>•</span>
                                  <span style={{ fontSize: '12px', color: mutedText }}>
                                    {formatDate(ticket.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginLeft: '12px', flexShrink: 0 }}>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  background: statusBadge.bg,
                                  border: `1px solid ${statusBadge.border}`,
                                  color: statusBadge.color,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {ticket.status.replace('_', ' ')}
                              </span>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  background: priorityBadge.bg,
                                  border: `1px solid ${priorityBadge.border}`,
                                  color: priorityBadge.color,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {ticket.priority}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '40px', textAlign: 'center' }}>
                        <Inbox size={32} style={{ color: mutedText, marginBottom: '8px' }} />
                        <div style={{ fontSize: '13px', color: mutedText }}>{t('support.noTickets')}</div>
                      </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '8px',
                          padding: '12px 16px',
                          borderTop: `1px solid ${sectionBorder}`,
                        }}
                      >
                        <span style={{ fontSize: '13px', color: mutedText }}>
                          {t('support.page')} {currentPage} {t('support.of')} {totalPages}
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              fontSize: '13px',
                              fontWeight: 500,
                              border: `1px solid ${secondaryButtonBorder}`,
                              borderRadius: '8px',
                              background: secondaryButtonBg,
                              color: secondaryButtonText,
                              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                              opacity: currentPage === 1 ? disabledOpacity : 1,
                            }}
                          >
                            <ChevronLeft size={14} />
                            {t('support.previous')}
                          </button>
                          <button
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              fontSize: '13px',
                              fontWeight: 500,
                              border: `1px solid ${secondaryButtonBorder}`,
                              borderRadius: '8px',
                              background: secondaryButtonBg,
                              color: secondaryButtonText,
                              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                              opacity: currentPage === totalPages ? disabledOpacity : 1,
                            }}
                          >
                            {t('support.next')}
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                   </div>
                  </div>
                </div>

                {/* Right Column - Help Articles */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    maxHeight: '600px',
                  }}
                >
                  {/* Help Header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      borderBottom: `1px solid ${sectionBorder}`,
                      flexShrink: 0,
                    }}
                  >
                    <HelpCircle size={16} style={{ color: iconColor }} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>
                      {t('support.helpArticles') || 'Help Articles'}
                    </span>
                  </div>

                  {/* Search Bar */}
                  <div style={{ padding: '12px 16px', borderBottom: `1px solid ${dividerColor}`, flexShrink: 0 }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={helpSearchQuery}
                        onChange={(e) => setHelpSearchQuery(e.target.value)}
                        placeholder={t('support.searchHelp') || 'Type to find help articles...'}
                        style={{
                          width: '100%',
                          padding: '8px 12px 8px 34px',
                          fontSize: '13px',
                          border: `1px solid ${inputBorder}`,
                          borderRadius: '8px',
                          background: inputBg,
                          color: inputText,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      <Search
                        size={14}
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: mutedText,
                        }}
                      />
                    </div>
                  </div>

                  {/* Articles List */}
                  <div
                    className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
                    style={{ flex: 1, overflowY: 'auto' }}
                  >
                    {filterArticles(helpData, helpSearchQuery).length > 0 ? (
                      filterArticles(helpData, helpSearchQuery).slice(0, 10).map((article, index: number) => (
                        <div
                          key={article.id}
                          onClick={() => handleArticleClick(article)}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${dividerColor}`,
                            transition: 'background 0.15s ease',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              width: '28px',
                              height: '28px',
                              background: iconContainerBg,
                              borderRadius: '8px',
                              color: iconColor,
                              marginTop: '2px',
                            }}
                          >
                            <HelpCircle size={14} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: '13px',
                                fontWeight: 500,
                                color: titleColor,
                                marginBottom: '2px',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {article.title}
                            </div>
                            <div
                              style={{
                                fontSize: '12px',
                                color: mutedText,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {article.categoryTitle}
                            </div>
                          </div>
                          <ChevronRight size={14} style={{ color: mutedText, flexShrink: 0, marginTop: '4px' }} />
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '40px', textAlign: 'center' }}>
                        <Search size={28} style={{ color: mutedText, marginBottom: '8px' }} />
                        <div style={{ fontSize: '13px', color: mutedText }}>
                          {t('support.noHelpArticles') || 'No articles found'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* View All Link */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '12px 16px',
                      borderTop: `1px solid ${dividerColor}`,
                      flexShrink: 0,
                      cursor: 'pointer',
                    }}
                    onClick={() => setActiveTab('help')}
                  >
                    <Book size={14} style={{ color: iconColor }} />
                    <span style={{ fontSize: '13px', fontWeight: 500, color: iconColor }}>
                      {t('support.viewAllHelp') || 'View Full Help Center'}
                    </span>
                    <ArrowRight size={14} style={{ color: iconColor }} />
                  </div>
                </div>
              </div>

              {/* Statistics Section */}
              {stats && (
                <div
                  style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: '12px',
                    padding: '20px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <BarChart3 size={16} style={{ color: iconColor }} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>
                      {t('support.statistics')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: '16px' }}>
                    {[
                      { value: stats.total_tickets, label: t('support.totalTickets'), color: iconColor },
                      { value: stats.open_tickets, label: t('support.openTickets'), color: iconColor },
                      { value: stats.in_progress_tickets, label: t('support.inProgressTickets'), color: isDark ? '#FCD34D' : '#E17100' },
                      { value: stats.resolved_tickets, label: t('support.resolvedTickets'), color: isDark ? '#6EE7B7' : '#009966' },
                    ].map((stat, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: '16px',
                          border: `1px solid ${sectionBorder}`,
                          borderRadius: '8px',
                        }}
                      >
                        <span style={{ fontSize: '24px', fontWeight: 700, color: stat.color }}>
                          {stat.value}
                        </span>
                        <span style={{ fontSize: '13px', color: mutedText, marginTop: '4px', textAlign: 'center' }}>
                          {stat.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Help Center Tab */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {!selectedHelpCategory ? (
                <>
                  {/* Help Category Search */}
                  <div style={{ position: 'relative', maxWidth: '400px' }}>
                    <input
                      type="text"
                      placeholder={t('help.searchPlaceholder')}
                      value={helpCategorySearchQuery}
                      onChange={(e) => setHelpCategorySearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px 8px 34px',
                        fontSize: '13px',
                        border: `1px solid ${inputBorder}`,
                        borderRadius: '8px',
                        background: inputBg,
                        color: inputText,
                        outline: 'none',
                        boxSizing: 'border-box',
                        height: '36px',
                      }}
                    />
                    <Search
                      size={14}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: mutedText,
                      }}
                    />
                  </div>

                  {/* Categories Grid */}
                  {filteredHelpCategories.length === 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: '12px',
                      }}
                    >
                      <Search size={36} style={{ color: mutedText, marginBottom: '12px' }} />
                      <div style={{ fontSize: '14px', color: titleColor, marginBottom: '4px' }}>
                        {t('help.noArticlesFound')}
                      </div>
                      <div style={{ fontSize: '13px', color: mutedText }}>
                        {t('help.tryDifferentSearch')}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: '16px' }}>
                      {filteredHelpCategories.map((category: Category) => (
                        <div
                          key={category.id}
                          onClick={() => handleHelpCategoryClick(category)}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            padding: '20px',
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = iconColor;
                            e.currentTarget.style.boxShadow = isDark
                              ? '0px 4px 12px rgba(43, 127, 255, 0.15)'
                              : '0px 4px 12px rgba(43, 127, 255, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = cardBorder;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              width: '36px',
                              height: '36px',
                              background: iconContainerBg,
                              borderRadius: '10px',
                            }}
                          >
                            <HelpCircle size={18} style={{ color: iconColor }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: titleColor, marginBottom: '4px' }}>
                              {category.title}
                            </div>
                            <div
                              style={{
                                fontSize: '13px',
                                color: mutedText,
                                marginBottom: '8px',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {category.description}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: 500, color: iconColor }}>
                              <span>
                                {category.articles.length} {category.articles.length !== 1 ? t('help.articles') : t('help.article')}
                              </span>
                              <ChevronRight size={14} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* Article View */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Back Button */}
                  <button
                    onClick={handleBackToHelpCategories}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: mutedText,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = iconColor)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = mutedText)}
                  >
                    <ArrowLeft size={16} />
                    {t('help.backToCategories')}
                  </button>

                  <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]" style={{ gap: '16px' }}>
                    {/* Articles Sidebar */}
                    <div
                      style={{
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        alignSelf: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          borderBottom: `1px solid ${sectionBorder}`,
                        }}
                      >
                        <HelpCircle size={14} style={{ color: iconColor }} />
                        <span style={{ fontSize: '13px', fontWeight: 600, color: titleColor }}>
                          {selectedHelpCategory.title}
                        </span>
                      </div>
                      <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {selectedHelpCategory.articles.map((article) => {
                          const isActive = selectedHelpArticle?.id === article.id;
                          return (
                            <button
                              key={article.id}
                              onClick={() => setSelectedHelpArticle(article)}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: isActive ? 500 : 400,
                                background: isActive ? activeItemBg : 'transparent',
                                border: isActive ? `1px solid ${activeItemBorder}` : '1px solid transparent',
                                color: isActive ? activeItemText : titleColor,
                                cursor: 'pointer',
                                transition: 'background 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                if (!isActive) e.currentTarget.style.background = hoverBg;
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive) e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              {article.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Article Content */}
                    <div>
                      {selectedHelpArticle && (
                        <div
                          style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: '12px',
                            padding: '24px',
                          }}
                        >
                          <h2 style={{ fontSize: '16px', fontWeight: 600, color: titleColor, marginBottom: '12px' }}>
                            {selectedHelpArticle.title}
                          </h2>
                          <p style={{ fontSize: '14px', color: titleColor, lineHeight: '1.6', marginBottom: '24px' }}>
                            {selectedHelpArticle.content}
                          </p>

                          {/* Steps */}
                          {selectedHelpArticle.steps && selectedHelpArticle.steps.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                                <ListOrdered size={16} style={{ color: iconColor }} />
                                <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>
                                  {t('help.steps')}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {selectedHelpArticle.steps.map((step: ArticleStep, index: number) => (
                                  <div
                                    key={index}
                                    style={{
                                      borderLeft: `2px solid ${stepBorderColor}`,
                                      paddingLeft: '16px',
                                    }}
                                  >
                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                      <span
                                        style={{
                                          flexShrink: 0,
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '999px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          color: '#FFFFFF',
                                          background: buttonBg,
                                        }}
                                      >
                                        {index + 1}
                                      </span>
                                      <span style={{ fontSize: '13px', color: titleColor, paddingTop: '2px', flex: 1, lineHeight: '1.5' }}>
                                        {step.description}
                                      </span>
                                    </div>

                                    {step.image && (
                                      <div style={{ marginLeft: '34px', marginBottom: '10px' }}>
                                        <div
                                          style={{
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            background: isDark ? '#1D293D' : '#F8FAFC',
                                          }}
                                        >
                                          <img
                                            src={step.image}
                                            alt={`Step ${index + 1}`}
                                            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                                            onError={(e) => {
                                              const target = e.target as HTMLImageElement;
                                              target.style.display = 'none';
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {step.video && (
                                      <div style={{ marginLeft: '34px' }}>
                                        <div
                                          style={{
                                            aspectRatio: '16/9',
                                            background: isDark ? '#1D293D' : '#F8FAFC',
                                            borderRadius: '8px',
                                            border: `1px solid ${cardBorder}`,
                                            overflow: 'hidden',
                                          }}
                                        >
                                          <iframe
                                            width="100%"
                                            height="100%"
                                            src={step.video.replace('youtu.be/', 'www.youtube.com/embed/').replace('watch?v=', 'embed/').split('&')[0].split('?')[0]}
                                            title={`Video tutorial step ${index + 1}`}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            style={{ width: '100%', height: '100%' }}
                                          ></iframe>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Help Footer — Contact Support */}
                          <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: `1px solid ${sectionBorder}` }}>
                            <div
                              style={{
                                padding: '16px',
                                background: infoBg,
                                border: `1px solid ${infoBorder}`,
                                borderRadius: '10px',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    width: '32px',
                                    height: '32px',
                                    background: isDark ? '#0F172B' : '#FFFFFF',
                                    borderRadius: '999px',
                                  }}
                                >
                                  <Info size={16} style={{ color: iconColor }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '14px', fontWeight: 600, color: titleColor, marginBottom: '4px' }}>
                                    {t('help.stillNeedHelp')}
                                  </div>
                                  <div style={{ fontSize: '13px', color: mutedText, marginBottom: '12px', lineHeight: '1.5' }}>
                                    {t('help.contactSupportMessage')}
                                  </div>
                                  <button
                                    onClick={handleContactSupport}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '8px 16px',
                                      fontSize: '13px',
                                      fontWeight: 500,
                                      border: 'none',
                                      borderRadius: '8px',
                                      background: buttonBg,
                                      color: '#FFFFFF',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <Mail size={14} />
                                    {t('help.contactSupport')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* ===== MODALS ===== */}

      {/* Search Help Articles Modal — portaled to body so it covers sidebar */}
      {showSearchModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowSearchModal(false); setTicketSearchQuery(''); } }}
        >
          <div
            style={{
              background: modalBg,
              borderRadius: '16px',
              border: `1px solid ${modalBorder}`,
              boxShadow: isDark
                ? '0px 8px 32px rgba(0,0,0,0.6)'
                : '0px 8px 32px rgba(0,0,0,0.15)',
              width: '100%',
              maxWidth: '700px',
              height: '95vh',
              maxHeight: '95vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: `1px solid ${sectionBorder}`,
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    background: iconContainerBg,
                    borderRadius: '12px',
                    flexShrink: 0,
                  }}
                >
                  <Search size={20} style={{ color: iconColor }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: titleColor }}>
                    {t('support.searchBeforeTicket') || 'Search Help Articles First'}
                  </div>
                  <div style={{ fontSize: '13px', color: mutedText, marginTop: '2px' }}>
                    {t('support.searchBeforeTicketSubtitle') || 'You might find your answer here'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setTicketSearchQuery('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: mutedText,
                  borderRadius: '8px',
                  flexShrink: 0,
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Search Input */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: `1px solid ${dividerColor}`,
                background: isDark ? '#1D293D' : '#F8FAFC',
                flexShrink: 0,
              }}
            >
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={ticketSearchQuery}
                  onChange={(e) => setTicketSearchQuery(e.target.value)}
                  placeholder={t('support.searchArticlesPlaceholder') || 'Search for help articles...'}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 14px 10px 38px',
                    fontSize: '14px',
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '8px',
                    background: inputBg,
                    color: inputText,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: mutedText,
                  }}
                />
                {ticketSearchQuery && (
                  <button
                    onClick={() => setTicketSearchQuery('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: mutedText,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                <Info size={12} style={{ color: mutedText }} />
                <span style={{ fontSize: '12px', color: mutedText }}>
                  {t('support.searchHint') || 'Try searching for keywords related to your issue'}
                </span>
              </div>
            </div>

            {/* Articles List */}
            <div
              className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
              style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}
            >
              {filterArticles(helpData, ticketSearchQuery).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: mutedText, marginBottom: '4px' }}>
                    {t('support.foundArticles') || 'Related Articles'} ({filterArticles(helpData, ticketSearchQuery).length})
                  </div>
                  {filterArticles(helpData, ticketSearchQuery).slice(0, 6).map((article) => (
                    <div
                      key={article.id}
                      onClick={() => {
                        handleArticleClick(article);
                        setShowSearchModal(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '14px',
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = iconColor)}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = cardBorder)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          width: '36px',
                          height: '36px',
                          background: iconContainerBg,
                          borderRadius: '10px',
                          color: iconColor,
                        }}
                      >
                        <HelpCircle size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: titleColor,
                            marginBottom: '4px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {article.title}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: mutedText,
                            marginBottom: '6px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {article.content}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span
                            style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              background: isDark ? '#1D293D' : '#F1F5F9',
                              color: mutedText,
                              borderRadius: '4px',
                            }}
                          >
                            {article.categoryTitle}
                          </span>
                          {article.steps && article.steps.length > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: iconColor }}>
                              <ListOrdered size={12} />
                              {article.steps.length} steps
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={16} style={{ color: mutedText, flexShrink: 0, marginTop: '2px' }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Search size={36} style={{ color: mutedText, marginBottom: '12px' }} />
                  <div style={{ fontSize: '14px', color: titleColor, marginBottom: '4px' }}>
                    {t('support.noArticlesFound') || 'No articles found'}
                  </div>
                  <div style={{ fontSize: '13px', color: mutedText }}>
                    {t('support.tryDifferentSearch') || 'Try different keywords or create a support ticket'}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '16px 20px',
                borderTop: `1px solid ${sectionBorder}`,
                background: modalFooterBg,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  padding: '12px',
                  background: infoBg,
                  border: `1px solid ${infoBorder}`,
                  borderRadius: '8px',
                  marginBottom: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <Info size={16} style={{ color: infoText, flexShrink: 0, marginTop: '1px' }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: titleColor, marginBottom: '2px' }}>
                      {t('support.cantFindAnswer') || "Can't find what you're looking for?"}
                    </div>
                    <div style={{ fontSize: '12px', color: mutedText }}>
                      {t('support.createTicketMessage') || 'No problem! Create a support ticket and our team will help you.'}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowSearchModal(false);
                    setTicketSearchQuery('');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: `1px solid ${secondaryButtonBorder}`,
                    borderRadius: '8px',
                    background: secondaryButtonBg,
                    color: secondaryButtonText,
                    cursor: 'pointer',
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleProceedToCreateTicket}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    fontWeight: 500,
                    border: 'none',
                    borderRadius: '8px',
                    background: buttonBg,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                  }}
                >
                  <Ticket size={14} />
                  {t('support.proceedToCreateTicket') || 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Ticket Modal — portaled to body so it covers sidebar */}
      {showCreateModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}
        >
          <div
            className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
            style={{
              background: modalBg,
              borderRadius: '16px',
              border: `1px solid ${modalBorder}`,
              boxShadow: isDark
                ? '0px 8px 32px rgba(0,0,0,0.6)'
                : '0px 8px 32px rgba(0,0,0,0.15)',
              width: '100%',
              maxWidth: '600px',
              height: '95vh',
              maxHeight: '95vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: `1px solid ${sectionBorder}`,
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    background: isDark ? '#3B1C6B' : '#F5F3FF',
                    borderRadius: '12px',
                    flexShrink: 0,
                  }}
                >
                  <PlusCircle size={20} style={{ color: isDark ? '#C084FC' : '#7C3AED' }} />
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: titleColor }}>
                    {t('support.createNewTicket')}
                  </div>
                  <div style={{ fontSize: '13px', color: mutedText, marginTop: '2px' }}>
                    {t('support.createTicketSubtitle')}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: mutedText,
                  borderRadius: '8px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: labelColor }}>
                  {t('support.category.label')} <span style={{ color: '#EF4444' }}>*</span>
                </span>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as CreateTicketRequest['category'] })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '8px',
                    background: inputBg,
                    color: inputText,
                    outline: 'none',
                    cursor: 'pointer',
                    appearance: 'auto',
                  }}
                >
                  <option value="technical_issue">{t('support.category.technical_issue')}</option>
                  <option value="billing">{t('support.category.billing')}</option>
                  <option value="account">{t('support.category.account')}</option>
                  <option value="feature_request">{t('support.category.feature_request')}</option>
                  <option value="bug_report">{t('support.category.bug_report')}</option>
                  <option value="general_inquiry">{t('support.category.general_inquiry')}</option>
                  <option value="other">{t('support.category.other')}</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: labelColor }}>
                  {t('support.priority.label')} <span style={{ color: '#EF4444' }}>*</span>
                </span>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as CreateTicketRequest['priority'] })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '8px',
                    background: inputBg,
                    color: inputText,
                    outline: 'none',
                    cursor: 'pointer',
                    appearance: 'auto',
                  }}
                >
                  <option value="low">{t('support.priority.low')}</option>
                  <option value="medium">{t('support.priority.medium')}</option>
                  <option value="high">{t('support.priority.high')}</option>
                  <option value="urgent">{t('support.priority.urgent')}</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: labelColor }}>
                  {t('support.subject')} <span style={{ color: '#EF4444' }}>*</span>
                </span>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder={t('support.subjectPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '8px',
                    background: inputBg,
                    color: inputText,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: labelColor }}>
                  {t('support.description')} <span style={{ color: '#EF4444' }}>*</span>
                </span>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('support.descriptionPlaceholder')}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: `1px solid ${inputBorder}`,
                    borderRadius: '8px',
                    background: inputBg,
                    color: inputText,
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    minHeight: '120px',
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: 'flex',
                gap: '10px',
                padding: '16px 20px',
                borderTop: `1px solid ${sectionBorder}`,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowSearchModal(true);
                }}
                disabled={isCreating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: `1px solid ${secondaryButtonBorder}`,
                  borderRadius: '8px',
                  background: secondaryButtonBg,
                  color: secondaryButtonText,
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                  opacity: isCreating ? disabledOpacity : 1,
                }}
              >
                <ArrowLeft size={16} />
                {t('support.backToSearch') || 'Back'}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={isCreating}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: `1px solid ${secondaryButtonBorder}`,
                  borderRadius: '8px',
                  background: secondaryButtonBg,
                  color: secondaryButtonText,
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                  opacity: isCreating ? disabledOpacity : 1,
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateTicket}
                disabled={isCreating}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: '8px',
                  background: isCreating ? (isDark ? '#314158' : '#90A1B9') : buttonBg,
                  color: '#FFFFFF',
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                }}
              >
                {isCreating ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    {t('support.creating')}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {t('support.submit')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Loading Spinner Overlay — portaled to body so it covers sidebar */}
      <LoadingSpinnerOverlay isVisible={isLoadingTicket} isDark={isDark} />


      {/* Ticket Details Modal — portaled to body so it covers sidebar */}
      {showDetailsModal && selectedTicket && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowDetailsModal(false); setComment(''); setSelectedFile(null); setShowReplySection(false); } }}
        >
          <div
            style={{
              background: modalBg,
              borderRadius: '16px',
              border: `1px solid ${modalBorder}`,
              boxShadow: isDark
                ? '0px 8px 32px rgba(0,0,0,0.6)'
                : '0px 8px 32px rgba(0,0,0,0.15)',
              width: '100%',
              maxWidth: '800px',
              height: '95vh',
              maxHeight: '95vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: `1px solid ${sectionBorder}`,
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    background: iconContainerBg,
                    borderRadius: '12px',
                    flexShrink: 0,
                    color: iconColor,
                  }}
                >
                  {getCategoryIcon(selectedTicket.category)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: titleColor,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedTicket.subject}
                  </div>
                  <div style={{ fontSize: '13px', color: mutedText, marginTop: '2px' }}>
                    #{selectedTicket.ticket_id || selectedTicket._id}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: mutedText,
                  borderRadius: '8px',
                  flexShrink: 0,
                  marginLeft: '12px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Top Section - Badges, Description, Jira */}
              <div style={{ padding: '16px 20px', flexShrink: 0 }}>
                {/* Badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  {(() => {
                    const sb = getStatusBadge(selectedTicket.status, isDark);
                    return (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 500,
                          background: sb.bg,
                          border: `1px solid ${sb.border}`,
                          color: sb.color,
                        }}
                      >
                        <Circle size={8} fill="currentColor" />
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                    );
                  })()}
                  {(() => {
                    const pb = getPriorityBadge(selectedTicket.priority, isDark);
                    return (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 500,
                          background: pb.bg,
                          border: `1px solid ${pb.border}`,
                          color: pb.color,
                        }}
                      >
                        <Flag size={10} />
                        {selectedTicket.priority}
                      </span>
                    );
                  })()}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 500,
                      background: isDark ? '#1D293D' : '#F8FAFC',
                      border: `1px solid ${cardBorder}`,
                      color: mutedText,
                    }}
                  >
                    {getCategoryIcon(selectedTicket.category)}
                    {selectedTicket.category.replace('_', ' ')}
                  </span>
                  {selectedTicket.jira_issue_key && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 500,
                        background: jiraBg,
                        border: `1px solid ${jiraBorder}`,
                        color: jiraText,
                      }}
                    >
                      <Link2 size={10} />
                      {selectedTicket.jira_issue_key}
                    </span>
                  )}
                </div>

                {/* Description */}
                <div
                  style={{
                    padding: '14px',
                    background: isDark ? '#1D293D' : '#F8FAFC',
                    borderRadius: '10px',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <FileText size={14} style={{ color: iconColor }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: titleColor }}>
                      {t('support.description')}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: titleColor, whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                    {selectedTicket.description}
                  </div>
                </div>

                {/* Jira Details */}
                {jiraDetails && (
                  <div
                    style={{
                      padding: '14px',
                      background: jiraBg,
                      border: `1px solid ${jiraBorder}`,
                      borderRadius: '10px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                      <Link2 size={14} style={{ color: jiraText }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: titleColor }}>
                        Jira Integration
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {[
                        { label: 'Issue Key', value: jiraDetails.key },
                        { label: 'Status', value: jiraDetails.status },
                        { label: 'Priority', value: jiraDetails.priority },
                        { label: 'Assignee', value: jiraDetails.assignee || 'Unassigned' },
                      ].map((item, i) => (
                        <div key={i}>
                          <div style={{ fontSize: '12px', color: mutedText, marginBottom: '2px' }}>{item.label}</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: titleColor }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Timeline - Scrollable */}
              <div
                className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
                style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}
              >
                {/* Timeline Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '12px 0',
                    position: 'sticky',
                    top: 0,
                    background: modalBg,
                    zIndex: 20,
                  }}
                >
                  <Clock size={14} style={{ color: iconColor }} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: titleColor }}>
                    {t('support.timeline')}
                  </span>
                </div>

                {ticketHistory.length > 0 ? (
                  <div style={{ position: 'relative', paddingBottom: '16px' }}>
                    {/* Timeline Line */}
                    <div
                      style={{
                        position: 'absolute',
                        left: '15px',
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        background: isDark ? '#314158' : '#E2E8F0',
                      }}
                    />

                    {/* Timeline Items */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {ticketHistory.map((event) => (
                        <div key={event._id} style={{ position: 'relative', display: 'flex', gap: '12px' }}>
                          {/* Event Icon */}
                          <div
                            style={{
                              position: 'relative',
                              zIndex: 10,
                              flexShrink: 0,
                              width: '32px',
                              height: '32px',
                              background: getEventColor(event.event_type),
                              borderRadius: '999px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {getEventIcon(event.event_type)}
                          </div>

                          {/* Event Content */}
                          <div
                            style={{
                              flex: 1,
                              padding: '12px',
                              background: cardBg,
                              border: `1px solid ${cardBorder}`,
                              borderRadius: '10px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13px', fontWeight: 500, color: titleColor }}>
                                  {event.description}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: mutedText, marginTop: '4px' }}>
                                  {event.changed_by.source === 'user' ? (
                                    <>
                                      <User size={12} />
                                      {event.changed_by.user_name || event.changed_by.user_email}
                                    </>
                                  ) : (
                                    <>
                                      <Settings size={12} />
                                      System
                                    </>
                                  )}
                                </div>
                              </div>
                              <span style={{ fontSize: '12px', color: mutedText, marginLeft: '8px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                {formatDate(event.created_at)}
                              </span>
                            </div>

                            {/* Comment */}
                            {event.comment && (
                              <div
                                style={{
                                  marginTop: '8px',
                                  padding: '10px',
                                  background: commentBg,
                                  border: `1px solid ${commentBorder}`,
                                  borderRadius: '8px',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                  <MessageSquareText size={14} style={{ color: isDark ? '#818CF8' : '#6366F1', flexShrink: 0, marginTop: '2px' }} />
                                  <span style={{ fontSize: '13px', color: titleColor, lineHeight: '1.5', flex: 1 }}>
                                    {event.comment}
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Jira webhook data */}
                            {event.jira_webhook_data && (
                              <div
                                style={{
                                  marginTop: '8px',
                                  padding: '8px 10px',
                                  background: jiraBg,
                                  border: `1px solid ${jiraBorder}`,
                                  borderRadius: '8px',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: jiraText }}>
                                  <Link2 size={12} />
                                  Jira: {event.jira_webhook_data.issue_key}
                                </div>
                              </div>
                            )}

                            {/* Changes data */}
                            {!event.comment && event.changes && event.changes.field && (
                              <div
                                style={{
                                  marginTop: '8px',
                                  padding: '10px',
                                  background: changeBg,
                                  border: `1px solid ${changeBorder}`,
                                  borderRadius: '8px',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                  <ArrowLeftRight size={14} style={{ color: iconColor, flexShrink: 0, marginTop: '2px' }} />
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '13px', fontWeight: 500, color: infoText, marginBottom: '4px' }}>
                                      Field Changed: <span style={{ textTransform: 'capitalize' }}>{event.changes.field.replace('_', ' ')}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                      <span style={{ color: mutedText }}>
                                        From: <span style={{ fontWeight: 500, color: titleColor }}>{String(event.changes.old_value ?? 'Not set')}</span>
                                      </span>
                                      <ArrowRight size={12} style={{ color: mutedText }} />
                                      <span style={{ color: mutedText }}>
                                        To: <span style={{ fontWeight: 500, color: titleColor }}>{String(event.changes.new_value ?? 'Not set')}</span>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <Clock size={28} style={{ color: mutedText, marginBottom: '8px' }} />
                    <div style={{ fontSize: '13px', color: mutedText }}>No timeline events available</div>
                  </div>
                )}
              </div>

              {/* Reply Section */}
              {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                <div style={{ padding: '0 20px 16px', flexShrink: 0 }}>
                  <div style={{ borderTop: `1px solid ${sectionBorder}`, paddingTop: '12px' }}>
                    {/* Toggle Button */}
                    <button
                      onClick={() => setShowReplySection(!showReplySection)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        fontSize: '13px',
                        fontWeight: 500,
                        background: isDark ? '#1D293D' : '#F8FAFC',
                        border: `1px solid ${cardBorder}`,
                        borderRadius: '8px',
                        color: titleColor,
                        cursor: 'pointer',
                        marginBottom: showReplySection ? '12px' : 0,
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Reply size={16} style={{ color: iconColor }} />
                        {t('support.addReply') || 'Add Reply'}
                      </span>
                      {showReplySection ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>

                    {/* Reply Form */}
                    {showReplySection && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <textarea
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder={t('support.replyPlaceholder') || 'Type your reply here...'}
                          rows={4}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: `1px solid ${inputBorder}`,
                            borderRadius: '8px',
                            background: inputBg,
                            color: inputText,
                            outline: 'none',
                            resize: 'none',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                          }}
                        />

                        {/* File Upload */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="file"
                            id="attachment-upload"
                            ref={fileInputRef}
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                          />
                          <label
                            htmlFor="attachment-upload"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              fontSize: '13px',
                              fontWeight: 500,
                              border: `1px solid ${secondaryButtonBorder}`,
                              borderRadius: '8px',
                              background: secondaryButtonBg,
                              color: secondaryButtonText,
                              cursor: 'pointer',
                            }}
                          >
                            <Paperclip size={14} />
                            {t('support.attachImage') || 'Attach Image'}
                          </label>
                          <span style={{ fontSize: '12px', color: mutedText }}>
                            {t('support.maxFileSize') || 'Max 1MB, images only'}
                          </span>
                        </div>

                        {/* Selected File Preview */}
                        {selectedFile && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '10px 12px',
                              background: fileBg,
                              border: `1px solid ${fileBorder}`,
                              borderRadius: '8px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                              <FileImage size={16} style={{ color: iconColor, flexShrink: 0 }} />
                              <span style={{ fontSize: '13px', color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {selectedFile.name}
                              </span>
                              <span style={{ fontSize: '12px', color: mutedText, flexShrink: 0 }}>
                                ({(selectedFile.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <button
                              onClick={handleFileRemove}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: mutedText,
                                marginLeft: '8px',
                              }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )}

                        {/* Submit Button */}
                        <button
                          onClick={handleCommentSubmit}
                          disabled={isSubmittingComment || isUploadingFile || (!comment.trim() && !selectedFile)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            border: 'none',
                            borderRadius: '8px',
                            background: (isSubmittingComment || isUploadingFile || (!comment.trim() && !selectedFile))
                              ? (isDark ? '#314158' : '#90A1B9')
                              : buttonBg,
                            color: '#FFFFFF',
                            cursor: (isSubmittingComment || isUploadingFile || (!comment.trim() && !selectedFile))
                              ? 'not-allowed'
                              : 'pointer',
                          }}
                        >
                          {isSubmittingComment || isUploadingFile ? (
                            <>
                              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                              {isUploadingFile ? (t('support.uploading') || 'Uploading...') : (t('support.submitting') || 'Submitting...')}
                            </>
                          ) : (
                            <>
                              <Send size={16} />
                              {t('support.submitReply') || 'Submit Reply'}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '12px 20px',
                borderTop: `1px solid ${sectionBorder}`,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setComment('');
                  setSelectedFile(null);
                  setShowReplySection(false);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: `1px solid ${secondaryButtonBorder}`,
                  borderRadius: '8px',
                  background: secondaryButtonBg,
                  color: secondaryButtonText,
                  cursor: 'pointer',
                }}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Help Article Modal — portaled to body so it covers sidebar */}
      {showArticleModal && selectedArticle && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowArticleModal(false); }}
        >
          <div
            style={{
              background: modalBg,
              borderRadius: '16px',
              border: `1px solid ${modalBorder}`,
              boxShadow: isDark
                ? '0px 8px 32px rgba(0,0,0,0.6)'
                : '0px 8px 32px rgba(0,0,0,0.15)',
              width: '100%',
              maxWidth: '700px',
              height: '95vh',
              maxHeight: '95vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: `1px solid ${sectionBorder}`,
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    background: iconContainerBg,
                    borderRadius: '12px',
                    flexShrink: 0,
                  }}
                >
                  <HelpCircle size={20} style={{ color: iconColor }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: titleColor,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {selectedArticle.title}
                  </div>
                  <div style={{ fontSize: '13px', color: mutedText, marginTop: '2px' }}>
                    {t('support.helpArticle') || 'Help Article'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowArticleModal(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: mutedText,
                  borderRadius: '8px',
                  flexShrink: 0,
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
              style={{ flex: 1, overflowY: 'auto', padding: '20px', minHeight: 0 }}
            >
              {/* Article Content */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', color: titleColor, lineHeight: '1.6' }}>
                  {selectedArticle.content}
                </div>
              </div>

              {/* Steps */}
              {selectedArticle.steps.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                    <ListOrdered size={16} style={{ color: iconColor }} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>
                      {t('support.steps') || 'Steps'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {selectedArticle.steps.map((step: ArticleStep, index: number) => (
                      <div
                        key={index}
                        style={{
                          borderLeft: `2px solid ${isDark ? '#314158' : '#BFDBFE'}`,
                          paddingLeft: '16px',
                        }}
                      >
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <span
                            style={{
                              flexShrink: 0,
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '999px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#FFFFFF',
                              background: buttonBg,
                            }}
                          >
                            {index + 1}
                          </span>
                          <span style={{ fontSize: '13px', color: titleColor, paddingTop: '2px', flex: 1, lineHeight: '1.5' }}>
                            {step.description}
                          </span>
                        </div>

                        {/* Step Image */}
                        {step.image && (
                          <div style={{ marginLeft: '32px', marginBottom: '8px' }}>
                            <div
                              style={{
                                border: `1px solid ${cardBorder}`,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                background: isDark ? '#1D293D' : '#F8FAFC',
                              }}
                            >
                              <img
                                src={step.image}
                                alt={`Step ${index + 1}`}
                                style={{ width: '100%', height: 'auto', objectFit: 'contain', minHeight: '120px', maxHeight: '300px' }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Step Video */}
                        {step.video && (
                          <div style={{ marginLeft: '32px' }}>
                            <div
                              style={{
                                aspectRatio: '16/9',
                                background: isDark ? '#1D293D' : '#F8FAFC',
                                borderRadius: '8px',
                                border: `1px solid ${cardBorder}`,
                                overflow: 'hidden',
                              }}
                            >
                              <iframe
                                width="100%"
                                height="100%"
                                src={step.video.replace('youtu.be/', 'www.youtube.com/embed/').replace('watch?v=', 'embed/').split('&')[0].split('?')[0]}
                                title={`Video Tutorial Step ${index + 1}`}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{ width: '100%', height: '100%' }}
                              ></iframe>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: 'flex',
                gap: '10px',
                padding: '16px 20px',
                borderTop: `1px solid ${sectionBorder}`,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setShowArticleModal(false)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: `1px solid ${secondaryButtonBorder}`,
                  borderRadius: '8px',
                  background: secondaryButtonBg,
                  color: secondaryButtonText,
                  cursor: 'pointer',
                }}
              >
                {t('common.close')}
              </button>
              <button
                onClick={() => { setShowArticleModal(false); setActiveTab('help'); }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: '8px',
                  background: buttonBg,
                  color: '#FFFFFF',
                  cursor: 'pointer',
                }}
              >
                <Book size={16} />
                {t('support.viewFullHelp') || 'View Full Help'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Support;
